import React from 'react';
import { getDrugIconPaths, getKeystoneIconPath } from '../../data/keystoneCards';
import { DRUG_ICONS } from '../../data/drugIcons';

/* Renders a drug's CANONICAL skill-tree-node icon, mirroring the node's own
   precedence in skillTreeLayout.js: the hand-authored 24-unit line-art
   (DRUG_ICON_PATHS, fill=none stroke) wins; the recraft filled silhouette
   (DRUG_ICONS) is the fallback for drugs without bespoke line-art. Color comes
   from currentColor so the host chip controls it. Returns null if neither
   exists — callers should fall back to a text label in that case. */
export function nodeIconExists(slug) {
  if (!slug) return false;
  const s = String(slug).toLowerCase();
  if (getKeystoneIconPath(s)) return true;
  const stroke = getDrugIconPaths(s);
  return (Array.isArray(stroke) && stroke.length > 0) || !!DRUG_ICONS[s];
}

export default function EffectNodeGlyph({ slug, size = 15, className = '' }) {
  if (!slug) return null;
  const s = String(slug).toLowerCase();

  // Keystones (vancomycin/metformin/adalimumab) live in KEYSTONE_ICON_PATHS,
  // NOT DRUG_ICON_PATHS — and they ALSO have recraft filled silhouettes in
  // DRUG_ICONS. Without resolving the keystone path first, the silhouette blob
  // wins and the tooltip glyph diverges from the skill-tree node (e.g. the
  // Vancomycin sigma rendered as a generic antibiotic fill). Resolve the
  // canonical keystone line-art first so the tooltip mirrors the node exactly
  // (same source ProgressMap/SkillTreeOwnedCluster use: getKeystoneIconPath).
  const keystonePath = getKeystoneIconPath(s);
  if (keystonePath) {
    return (
      <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        <path d={keystonePath} />
      </svg>
    );
  }

  const strokePaths = getDrugIconPaths(s);
  if (Array.isArray(strokePaths) && strokePaths.length > 0) {
    return (
      <svg
        className={className}
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
      >
        {strokePaths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
    );
  }

  const entry = DRUG_ICONS[s];
  if (entry) {
    return (
      <svg
        className={className}
        width={size}
        height={size}
        viewBox={entry.viewBox}
        fill="currentColor"
        stroke="none"
        aria-hidden="true"
      >
        {entry.paths.map((d, i) => (
          <path key={i} d={d} />
        ))}
      </svg>
    );
  }

  return null;
}
